/**
 * @fileoverview URL ve Path Parsing Fonksiyonlari
 *
 * Bu modul Emlakjet URL yapisi ve filtre string'lerini parse eden
 * merkezi fonksiyonlari saglar. useListingSearch ve filters modullerinde
 * kullanilir.
 *
 * @module utils/parsing
 *
 * @example
 * import { parseSearchPath, parseFiltersToObject, extractFilterStats } from './parsing';
 *
 * const location = parseSearchPath("satilik-konut/istanbul-kadikoy");
 * const filters = parseFiltersToObject("oda-sayisi=3-1&fiyat-max=5000000");
 * const stats = extractFilterStats("oda-sayisi=3-1&fiyat-max=5000000");
 */

/**
 * Arama yolunu (path) parse ederek konum bilgisi cikarir.
 *
 * Emlakjet URL yapisi: `{islem}-{tip}/{il}-{ilce}-{mahalle}`
 * Ornegin: "satilik-konut/istanbul-kadikoy-fenerbahce"
 *
 * @function parseSearchPath
 * @param {string|null} path - Arama yolu
 * @returns {Object|null} Parse edilmis konum bilgisi
 * @returns {string|null} returns.islem - Islem tipi (satilik/kiralik)
 * @returns {string|null} returns.tip - Emlak tipi (konut/isyeri/arsa)
 * @returns {string|null} returns.il - Il adi
 * @returns {string|null} returns.ilce - Ilce adi
 * @returns {string|null} returns.mahalle - Mahalle adi
 *
 * @example
 * parseSearchPath("satilik-konut/istanbul-kadikoy")
 * // Returns: { islem: "satilik", tip: "konut", il: "istanbul", ilce: "kadikoy", mahalle: null }
 *
 * @example
 * parseSearchPath("kiralik-isyeri/ankara-cankaya-kizilay")
 * // Returns: { islem: "kiralik", tip: "isyeri", il: "ankara", ilce: "cankaya", mahalle: "kizilay" }
 */
export function parseSearchPath(path) {
  if (!path) return null;

  const parts = path.split("/");
  const [typeSegment, locationSegment] = parts;

  // Parse type segment (e.g., "satilik-konut")
  const typeParts = typeSegment?.split("-") || [];
  const islem = typeParts[0] || null; // satilik/kiralik
  const tip = typeParts.slice(1).join("-") || null; // konut/isyeri/arsa

  // Parse location segment (e.g., "istanbul-kadikoy-fenerbahce")
  const locationParts = locationSegment?.split("-") || [];
  const il = locationParts[0] || null;
  const ilce = locationParts[1] || null;
  const mahalle = locationParts.slice(2).join("-") || null;

  return { islem, tip, il, ilce, mahalle };
}

/**
 * Filtre string'ini parse ederek objeye cevirir.
 *
 * "key1=val1,val2&key2=val3" formatindaki string'i
 * { key1: ["val1", "val2"], key2: ["val3"] } seklinde parse eder.
 *
 * Bu fonksiyon `filters.js` dosyasindaki `parseFilterString` ile
 * ayni islevi gorur, ancak burada merkezi olarak tanimlanmistir.
 *
 * @function parseFiltersToObject
 * @param {string|null|undefined} raw - Ham filtre string'i
 * @returns {Object.<string, string[]>} Parse edilmis filtre objesi
 *
 * @example
 * parseFiltersToObject("oda-sayisi=3-1,4-1&min-fiyat=1000000")
 * // { "oda-sayisi": ["3-1", "4-1"], "min-fiyat": ["1000000"] }
 */
export function parseFiltersToObject(raw) {
  if (typeof raw !== "string" || raw.trim().length === 0) return {};
  const out = {};
  const pairs = raw.split("&").map((p) => p.trim()).filter(Boolean);
  for (const pair of pairs) {
    const [k, ...rest] = pair.split("=");
    if (!k || rest.length === 0) continue;
    const v = rest.join("=");
    const values = v
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    out[k] = values;
  }
  return out;
}

/**
 * Filtre string'inden istatistik ve spesifik degerler cikarir.
 *
 * Bu fonksiyon `parseFiltersToObject`'den farkli olarak, sadece
 * belirli alanlari cikarir ve toplam filtre sayisini hesaplar.
 * Alert ve bildirim sistemlerinde kullanilir.
 *
 * @function extractFilterStats
 * @param {string|null|undefined} filters - Filtre string'i
 * @returns {Object} Parse sonucu
 * @returns {Object} returns.parsed - Parse edilmis spesifik filtre degerleri
 * @returns {string|null} returns.parsed.odaSayisi - Oda sayisi (3+1 formatinda)
 * @returns {number|null} returns.parsed.fiyatMin - Minimum fiyat
 * @returns {number|null} returns.parsed.fiyatMax - Maksimum fiyat
 * @returns {number|null} returns.parsed.alanMin - Minimum alan (m2)
 * @returns {number|null} returns.parsed.alanMax - Maksimum alan (m2)
 * @returns {number} returns.count - Toplam filtre sayisi
 *
 * @example
 * extractFilterStats("oda-sayisi=3-1&fiyat-max=5000000")
 * // { parsed: { odaSayisi: "3+1", fiyatMax: 5000000 }, count: 2 }
 */
export function extractFilterStats(filters) {
  if (!filters) return { parsed: {}, count: 0 };

  const result = {};
  const params = new URLSearchParams(filters.replace(/&/g, "&"));
  let count = 0;

  for (const [key, value] of params.entries()) {
    count++;
    if (key === "oda-sayisi") {
      result.odaSayisi = value.replace("-", "+");
    } else if (key === "fiyat-min") {
      result.fiyatMin = parseInt(value, 10) || null;
    } else if (key === "fiyat-max") {
      result.fiyatMax = parseInt(value, 10) || null;
    } else if (key === "metrekare-min") {
      result.alanMin = parseInt(value, 10) || null;
    } else if (key === "metrekare-max") {
      result.alanMax = parseInt(value, 10) || null;
    }
  }

  return { parsed: result, count };
}
